import { useEffect, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Archive, ArrowLeft, ArrowRight, Check, ChevronLeft, ChevronRight, FileText, Pencil, RotateCcw, Send, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CandidateDrawer, type DrawerCandidate } from '@/lib/CandidateDrawer'
import { Markdown } from '@/lib/Markdown'
import { type DeliveryStage, type JobDetail, type JobShortlistCandidate, type JobStage, type SubmissionDraft, DELIVERY_STAGES, deliveryStageIndex, deleteJob as apiDeleteJob, draftSubmission, getJob, selectCandidate, setJobStage, updateJob } from '@/lib/api'
import { STAGE_META, STAGE_ORDER, StageBadge } from '@/lib/rolesTable'
import { atsNav } from '@/lib/atsNav'
import { Avatar } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/jobs/$jobId')({ component: JobPage })

const fmtExp = (m: number | null | undefined) => (m == null ? '—' : `${Math.floor(m / 12)}y ${m % 12}m`)
const verdictVariant = (v: string | null): 'success' | 'warning' | 'error' | 'info' =>
  v === 'strong' ? 'success' : v === 'weak' ? 'error' : v === 'maybe' ? 'warning' : 'info'

function JobPage() {
  const { jobId } = Route.useParams()
  const navigate = useNavigate()
  const { data: job, isLoading, refetch } = useQuery({ queryKey: ['job', jobId], queryFn: () => getJob(jobId), refetchOnMount: 'always' })
  const [openCand, setOpenCand] = useState<DrawerCandidate | null>(null)
  const [jdView, setJdView] = useState<'formatted' | 'raw'>('formatted')
  const [minScore, setMinScore] = useState(55) // 55 = "maybe and up" (matches the calibrated screening buckets)
  // Which delivery cohort the candidate list shows. Defaults to the job's current (latest) stage so the
  // recruiter sees exactly the candidates at the gate they're working — switchable by clicking a pipeline node.
  const [stageFilter, setStageFilter] = useState<DeliveryStage>('screening')
  useEffect(() => {
    if (!job) return
    const jobDelivery = DELIVERY_STAGES.includes(job.stage as DeliveryStage) ? (job.stage as DeliveryStage) : 'screening'
    // Land on the job's stage cohort — unless it's empty (job advanced ahead of its candidates), in which
    // case fall back to the furthest gate that actually has candidates so the list is never blank by default.
    let target = jobDelivery
    if (!(job.stageCounts[jobDelivery] > 0)) {
      for (let i = DELIVERY_STAGES.length - 1; i >= 0; i--) {
        if (job.stageCounts[DELIVERY_STAGES[i]] > 0) { target = DELIVERY_STAGES[i]; break }
      }
    }
    setStageFilter(target)
  }, [job?.stage])
  const [page, setPage] = useState(0)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [submission, setSubmission] = useState<SubmissionDraft | null>(null)
  const [drafting, setDrafting] = useState(false)
  useEffect(() => setPage(0), [minScore, stageFilter])
  const [sel, setSel] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (job?.shortlist) setSel(new Set(job.shortlist.filter((c) => c.selected).map((c) => c.candidateId)))
  }, [job?.shortlist])
  const toggleSelect = async (candidateId: string, next: boolean) => {
    setSel((prev) => { const n = new Set(prev); if (next) n.add(candidateId); else n.delete(candidateId); return n })
    try {
      await selectCandidate(jobId, candidateId, next)
    } catch {
      toast.error('Could not update selection')
      setSel((prev) => { const n = new Set(prev); if (next) n.delete(candidateId); else n.add(candidateId); return n })
    }
  }

  const onDraftSubmission = async () => {
    setDrafting(true)
    try {
      const r = await draftSubmission(jobId)
      if (r.draft) setSubmission(r.draft)
      toast.success(r.mode === 'auto' ? 'Submitted to client' : r.mode === 'off' ? 'Submission is set to Off in Automation' : 'Draft ready — queued for your approval in Automation')
      if (r.mode === 'auto') refetch()
    } catch {
      toast.error('Could not draft a submission — has it been screened?')
    } finally {
      setDrafting(false)
    }
  }

  const changeStage = async (stage: JobStage) => {
    setBusy(true)
    try {
      const r = await setJobStage(jobId, stage)
      await refetch()
      toast.success(r.promoted ? `${r.promoted} candidate${r.promoted === 1 ? '' : 's'} advanced to ${STAGE_META[stage].label}` : `Moved to ${STAGE_META[stage].label}`)
    } catch {
      toast.error('Could not update the stage.')
    } finally {
      setBusy(false)
    }
  }
  const onDelete = async () => {
    setBusy(true)
    try {
      await apiDeleteJob(jobId)
      toast.success('Job deleted')
      navigate({ to: '/jobs' })
    } catch {
      toast.error('Could not delete the job.')
      setBusy(false)
    }
  }

  const shortlist = job?.shortlist ?? []
  const stageCounts = job?.stageCounts ?? { screening: 0, shortlisted: 0, submitted: 0, interview: 0, placement: 0 }
  // Cohort = candidates whose furthest gate has REACHED the active filter stage (so "submitted" shows
  // everyone who got to submitted or beyond), then narrowed by the score threshold.
  const filterIdx = deliveryStageIndex(stageFilter)
  const filtered = shortlist.filter((c) => (c.score ?? 0) >= minScore && deliveryStageIndex(c.deliveryStage) >= filterIdx)
  const lastPage = Math.max(0, Math.ceil(filtered.length / 10) - 1)
  const pageRows = filtered.slice(page * 10, page * 10 + 10)

  return (
    <DashboardLayout title="Job" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <Link to="/jobs" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Jobs
      </Link>

      {isLoading || !job ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          {/* Header */}
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-1.5">
                {job.systemRef && <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[11px] text-foreground">{job.systemRef}</span>}
                {job.ref && <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{job.ref}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold text-foreground">{job.requirements.title}</h1>
                <StageBadge stage={job.stage} />
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{[job.company, job.owner, job.requirements.location].filter(Boolean).join(' · ') || '—'}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setEditing(true)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-2.5 py-2 text-sm text-foreground hover:bg-muted/50 disabled:opacity-50"><Pencil className="h-3.5 w-3.5" /> Edit</button>
              <button onClick={() => setConfirmDelete(true)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-2.5 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50" aria-label="Delete job"><Trash2 className="h-3.5 w-3.5" /></button>
              {job.screened && (
                <button onClick={onDraftSubmission} disabled={drafting} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-2.5 py-2 text-sm text-foreground hover:bg-muted/50 disabled:opacity-50"><Send className="h-3.5 w-3.5" /> {drafting ? 'Drafting…' : 'Submit'}</button>
              )}
              <Link to="/screen" search={{ jobId }} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
                <Sparkles className="h-4 w-4" /> {job.screened ? 'Re-run' : 'Screen'}
              </Link>
            </div>
          </div>

          {/* Delivery pipeline + per-stage funnel counts; nodes double as the candidate-cohort filter */}
          <StagePipeline stage={job.stage} counts={stageCounts} filterStage={stageFilter} onFilterStage={setStageFilter} busy={busy} onChange={changeStage} />

          {/* JD (left) + Candidates (right) */}
          <div className="grid gap-5 lg:grid-cols-2">
            {/* Left: requirements + JD */}
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-4 pb-4">
                  <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Requirements</h2>
                  <div className="flex flex-wrap gap-1.5">
                    {job.requirements.mustHave.map((s) => (
                      <span key={s} className="inline-flex items-center rounded border border-info/40 bg-info/10 px-2 py-0.5 text-xs text-info">{s}<span className="ml-1 opacity-60">must</span></span>
                    ))}
                    {(job.requirements.niceToHave ?? []).map((s) => (
                      <span key={s} className="inline-flex items-center rounded border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">{s}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h2 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" /> Job description
                    </h2>
                    {job.jdFormatted && job.jdRaw && (
                      <div className="flex items-center rounded-md border border-border p-0.5 text-xs">
                        {(['formatted', 'raw'] as const).map((v) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setJdView(v)}
                            className={`rounded px-2 py-0.5 capitalize transition-colors ${jdView === v ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {(() => {
                    const showFormatted = jdView === 'formatted' && job.jdFormatted
                    if (showFormatted) return <Markdown source={job.jdFormatted as string} className="max-h-[520px] space-y-2 overflow-y-auto pr-1" />
                    if (job.jdRaw) return <p className="max-h-[520px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-foreground">{job.jdRaw}</p>
                    if (job.jdFormatted) return <Markdown source={job.jdFormatted} className="max-h-[520px] space-y-2 overflow-y-auto pr-1" />
                    return <p className="text-sm text-muted-foreground">No JD attached. Paste one when creating the job to keep it here.</p>
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Right: candidate shortlist */}
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center gap-2 px-4 pt-4 pb-3">
                  <span className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-primary" /> Candidates</span>
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <span className="font-semibold tabular-nums text-foreground">{filtered.length}</span>
                    {STAGE_META[stageFilter].label.toLowerCase()}{stageFilter !== 'screening' ? '+' : ''}
                    {minScore > 0 && <span className="text-muted-foreground/70">· ≥{minScore}</span>}
                  </span>
                  {sel.size > 0 && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">{sel.size} selected</span>}
                  <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                    Min score
                    <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="h-8 rounded-md border border-input bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring" aria-label="Minimum score">
                      {[0, 55, 70, 80, 90].map((v) => (
                        <option key={v} value={v}>{v === 0 ? 'All' : v === 55 ? 'Maybe+ (55)' : v === 80 ? 'Strong (80)' : `${v}+`}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {shortlist.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Not screened yet. <Link to="/screen" search={{ jobId }} className="text-primary underline">Start screening</Link> to build a shortlist.
                  </div>
                ) : (
                  <>
                    <div className="max-h-[520px] divide-y divide-border overflow-y-auto border-t border-border">
                      {pageRows.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-muted-foreground">No candidates score ≥ {minScore}.</div>
                      ) : (
                        pageRows.map((c, i) => (
                          <div key={c.candidateId} className={`flex items-start gap-2 px-4 py-2.5 transition-colors hover:bg-muted/40 ${sel.has(c.candidateId) ? 'bg-primary/5' : ''}`}>
                            <input
                              type="checkbox"
                              checked={sel.has(c.candidateId)}
                              onChange={(e) => toggleSelect(c.candidateId, e.target.checked)}
                              className="mt-1.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
                              aria-label={`Select ${c.name ?? 'candidate'}`}
                            />
                            <button onClick={() => navigate({ to: '/compare/$jobId/$candidateId', params: { jobId, candidateId: c.candidateId } })} className="flex flex-1 items-start gap-3 text-left">
                              <span className="mt-1 w-5 shrink-0 text-xs text-muted-foreground tabular-nums">{page * 10 + i + 1}</span>
                              <Avatar name={c.name ?? '?'} className="mt-0.5 h-7 w-7 shrink-0 text-[10px]" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="min-w-0 truncate font-medium text-foreground">{c.name ?? '—'}</span>
                                  <Badge variant={verdictVariant(c.verdict)}>{c.verdict ?? 'scored'}</Badge>
                                  <span className="ml-auto text-sm font-semibold tabular-nums">{c.score}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="min-w-0 truncate text-xs text-muted-foreground">{[c.employer, fmtExp(c.totalMonthsExp), c.location].filter(Boolean).join(' · ')}</span>
                                  {/* The candidate's latest (furthest) delivery tag — accumulates as they advance */}
                                  <span className={`ml-auto inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${STAGE_META[c.deliveryStage].chip}`} title={`Furthest stage reached: ${STAGE_META[c.deliveryStage].label}`}>
                                    <span className={`h-1 w-1 rounded-full ${STAGE_META[c.deliveryStage].dot}`} />
                                    {STAGE_META[c.deliveryStage].label}
                                  </span>
                                </div>
                                {c.rationale && <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.rationale.replace(/\*\*/g, '')}</div>}
                              </div>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    {filtered.length > 10 && (
                      <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
                        <span>Showing {page * 10 + 1}–{Math.min((page + 1) * 10, filtered.length)} of {filtered.length}</span>
                        <div className="flex items-center gap-1">
                          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50"><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
                          <span className="px-1.5">Page {page + 1} of {lastPage + 1}</span>
                          <button disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50">Next <ChevronRight className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <CandidateDrawer
        cand={openCand}
        onClose={() => setOpenCand(null)}
        selected={openCand ? sel.has(openCand.candidateId) : false}
        onToggleSelect={openCand ? () => toggleSelect(openCand.candidateId, !sel.has(openCand.candidateId)) : undefined}
      />
      {editing && job && <EditJobDrawer job={job} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refetch() }} />}
      {submission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSubmission(null)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="flex items-center gap-1.5 text-base font-semibold text-foreground"><Send className="h-4 w-4 text-primary" /> Client submission — {submission.title}{submission.company ? ` · ${submission.company}` : ''}</h3>
            <p className="mt-1 text-xs text-muted-foreground">Anonymised — full contact shared on shortlisting. Queued for your approval in <span className="font-medium text-foreground">Automation</span>.</p>
            <pre className="mt-3 whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs leading-relaxed text-foreground">{submission.coverNote}</pre>
            <div className="mt-3 space-y-1.5">
              {submission.candidates.map((c) => (
                <div key={c.handle} className="rounded-md border border-border p-2 text-xs">
                  <div className="flex items-center justify-between"><span className="font-medium text-foreground">{c.handle}</span><span className="tabular-nums text-muted-foreground">fit {c.fit}</span></div>
                  <div className="text-muted-foreground">{c.headline}</div>
                  {c.skills.length > 0 && <div className="mt-0.5 flex flex-wrap gap-1">{c.skills.map((s) => <span key={s} className="rounded bg-muted/50 px-1 py-0.5 text-[11px] text-muted-foreground">{s}</span>)}</div>}
                </div>
              ))}
            </div>
            <div className="mt-4 text-right"><button onClick={() => setSubmission(null)} className="rounded-md border border-input bg-card px-3 py-2 text-sm hover:bg-muted/50">Close</button></div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setConfirmDelete(false)}>
          <div className="w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground">Delete this job?</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">This permanently removes the role and its screening shortlist. This can't be undone. To keep it but hide it, archive instead.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(false)} disabled={busy} className="rounded-md border border-input bg-card px-3 py-2 text-sm hover:bg-muted/50 disabled:opacity-50">Cancel</button>
              <button onClick={onDelete} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}

// Horizontal delivery pipeline: sourcing → screening → shortlisted → submitted → interview → placement,
// plus archive. Each gate shows its cumulative funnel count below it (candidates who reached it), the job's
// current stage is emphasised, and clicking a gate filters the candidate list to that cohort. The job only
// MOVES via "Advance" (forward, carrying the selected set) or Archive — clicking a node just changes the view.
function StagePipeline({ stage, counts, filterStage, onFilterStage, busy, onChange }: {
  stage: JobStage
  counts: Record<string, number>
  filterStage: DeliveryStage
  onFilterStage: (s: DeliveryStage) => void
  busy: boolean
  onChange: (s: JobStage) => void
}) {
  const archived = stage === 'archived'
  const curIdx = archived ? -1 : STAGE_ORDER.indexOf(stage)
  const next = !archived && curIdx >= 0 && curIdx < STAGE_ORDER.length - 1 ? STAGE_ORDER[curIdx + 1] : null
  return (
    <Card className="mb-5">
      <CardContent className="flex flex-wrap items-start gap-x-4 gap-y-3 py-3">
        <div className="flex min-w-0 flex-1 items-start">
          {STAGE_ORDER.map((s, i) => {
            const done = !archived && i < curIdx
            const current = !archived && i === curIdx
            const meta = STAGE_META[s] ?? STAGE_META.sourcing
            const isCohort = s !== 'sourcing' // candidates live in screening..placement; sourcing is pre-candidate
            const count = isCohort ? counts[s] ?? 0 : null
            const isFilter = isCohort && s === filterStage
            return (
              <div key={s} className="flex flex-1 items-start last:flex-none">
                <div className="flex shrink-0 flex-col items-center gap-1">
                  <button
                    onClick={() => isCohort && onFilterStage(s as DeliveryStage)}
                    disabled={busy}
                    title={isCohort ? `View ${meta.label.toLowerCase()} candidates` : meta.label}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60 ${isFilter ? 'ring-2 ring-primary ring-offset-1 ring-offset-card' : ''} ${current ? meta.chip : done ? 'border-success/40 bg-success/10 text-success' : 'border-border bg-card text-muted-foreground hover:bg-muted/50'} ${isCohort ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    {done ? <Check className="h-3 w-3" /> : <span className={`h-1.5 w-1.5 rounded-full ${current ? meta.dot : 'bg-muted-foreground/40'}`} />}
                    {meta.label}
                  </button>
                  <span className={`text-[11px] tabular-nums ${count == null ? 'invisible' : isFilter ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{count ?? 0}</span>
                </div>
                {i < STAGE_ORDER.length - 1 && <span className={`mx-1 mt-3 h-px flex-1 ${done ? 'bg-success/40' : 'bg-border'}`} />}
              </div>
            )
          })}
        </div>
        <div className="flex items-center gap-1.5 pt-1">
          {archived ? (
            <button onClick={() => onChange('screening')} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-2.5 py-1.5 text-xs font-medium hover:bg-muted/50 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Reopen</button>
          ) : (
            <>
              {next && <button onClick={() => onChange(next)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">Advance to {STAGE_META[next]?.label ?? next} <ArrowRight className="h-3.5 w-3.5" /></button>}
              <button onClick={() => onChange('archived')} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 disabled:opacity-50"><Archive className="h-3.5 w-3.5" /> Archive</button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

const splitSkills = (s: string): string[] => s.split(',').map((x) => x.trim()).filter(Boolean)

// Slide-over to edit a role's core fields + skills.
function EditJobDrawer({ job, onClose, onSaved }: { job: JobDetail; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(job.requirements.title)
  const [company, setCompany] = useState(job.company ?? '')
  const [location, setLocation] = useState(job.requirements.location ?? '')
  const [must, setMust] = useState(job.requirements.mustHave.join(', '))
  const [nice, setNice] = useState((job.requirements.niceToHave ?? []).join(', '))
  const [saving, setSaving] = useState(false)
  const inputCls = 'mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

  const save = async () => {
    if (!title.trim() || !splitSkills(must).length) {
      toast.error('A title and at least one must-have skill are required.')
      return
    }
    setSaving(true)
    try {
      await updateJob(job.id, {
        title: title.trim(),
        company: company.trim() || null,
        location: location.trim() || null,
        mustHave: splitSkills(must),
        niceToHave: splitSkills(nice),
      })
      toast.success('Job updated')
      onSaved()
    } catch {
      toast.error('Could not save changes.')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-4 text-base font-semibold text-foreground">Edit role</h3>
        <label className="block text-xs font-medium text-muted-foreground">Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
        </label>
        <label className="mt-3 block text-xs font-medium text-muted-foreground">Company
          <input value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} placeholder="e.g. Roche" />
        </label>
        <label className="mt-3 block text-xs font-medium text-muted-foreground">Location
          <input value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} placeholder="e.g. Hyderabad" />
        </label>
        <label className="mt-3 block text-xs font-medium text-muted-foreground">Must-have skills (comma-separated)
          <input value={must} onChange={(e) => setMust(e.target.value)} className={inputCls} />
        </label>
        <label className="mt-3 block text-xs font-medium text-muted-foreground">Nice-to-have skills (comma-separated)
          <input value={nice} onChange={(e) => setNice(e.target.value)} className={inputCls} />
        </label>
        <p className="mt-2 text-[11px] text-muted-foreground">Changing skills updates the requirements used for the next screening run.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="rounded-md border border-input bg-card px-3 py-2 text-sm hover:bg-muted/50 disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={saving} className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  )
}

function toDrawer(c: JobShortlistCandidate): DrawerCandidate {
  return {
    candidateId: c.candidateId,
    name: c.name,
    employer: c.employer,
    totalMonthsExp: c.totalMonthsExp,
    location: c.location,
    verdict: (c.verdict as 'strong' | 'maybe' | 'weak' | null) ?? null,
    score: c.score ?? undefined,
    rationale: c.rationale ?? undefined,
    confidence: c.confidence,
    strengths: c.strengths,
    concerns: c.concerns,
    pointsToDiscuss: c.pointsToDiscuss,
  }
}
